/**
 * Сервис аутентификации EyeHub (SSO-only через Keycloak PKCE / BFF).
 *
 * АРХИТЕКТУРНОЕ РЕШЕНИЕ: raw fetch вместо apiClient
 * ==================================================
 * Auth-операции (login/logout/checkAuthStatus) используют fetch(), а не apiClient:
 *
 *  1. Предотвращение циклических редиректов — apiClient при 401 редиректит на
 *     /login; если checkAuthStatus пойдёт через apiClient и получит 401, будет
 *     бесконечный цикл.
 *  2. Избежание circular dependency — apiClient (interceptor) косвенно зависит
 *     от auth-потока.
 *  3. Прямой контроль над cookie-сессией (credentials: 'include') без «магии»
 *     interceptors.
 *
 * Портал EyeHub — SSO-only: локального email/password логина нет, login()
 * всегда стартует Keycloak flow.
 */

import { bffAuthService } from "./bffAuthService";
import { AUTH_ENDPOINTS, API_BASE_PATH } from "../api/constants";
import { logger } from "../utils/logger";

class AuthService {
  /**
   * Инициирует логин. EyeHub — SSO-only: всегда Keycloak redirect.
   */
  async login() {
    return bffAuthService.initiateLogin();
  }

  /**
   * Выход из системы через unified logout endpoint.
   *
   * Backend определяет тип сессии и возвращает redirect:
   *   - { redirect_url: "/login" } — очищает eyehub_session cookie;
   *   - { redirect_to_keycloak: true, logout_url } — Keycloak SLO,
   *     фронт делает redirect для завершения SSO-сессии.
   */
  async logout() {
    let redirectTo = "/login";
    try {
      const response = await fetch(`${API_BASE_PATH}${AUTH_ENDPOINTS.LOGOUT}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        const body = await response.json().catch(() => ({}));
        const data = body?.data || {};
        if (
          data.redirect_to_keycloak &&
          data.logout_url &&
          this._isTrustedLogoutUrl(data.logout_url)
        ) {
          redirectTo = data.logout_url;
        } else if (data.redirect_url) {
          redirectTo = data.redirect_url;
        }
      } else {
        logger.warn(`[AUTH] Logout responded ${response.status}, clearing client state anyway`);
      }
    } catch (error) {
      logger.error("[AUTH] Logout error:", error);
    }

    this.clearAllAuthCookies();
    // Даём браузеру обработать Set-Cookie заголовки от ответа.
    await new Promise((resolve) => setTimeout(resolve, 100));
    window.location.href = redirectTo;
  }

  /**
   * Проверяет, доверенный ли logout URL для redirect после Keycloak SLO.
   * Допускаются relative paths и Keycloak realm URLs.
   */
  _isTrustedLogoutUrl(url) {
    if (!url || typeof url !== "string") return false;
    if (url.startsWith("/") && !url.startsWith("//")) return true;
    try {
      const parsed = new URL(url);
      return (
        parsed.pathname.includes("/realms/") &&
        parsed.pathname.includes("/protocol/openid-connect/")
      );
    } catch {
      return false;
    }
  }

  /**
   * Defensive-очистка cookie на клиенте (HttpOnly cookies очищает сервер через
   * Set-Cookie max-age=0).
   */
  clearAllAuthCookies() {
    const cookiesToClear = ["eyehub_session", "csrftoken"];
    cookiesToClear.forEach((cookieName) => {
      const deleteCookie = (name, path = "/", domain = "") => {
        const domainPart = domain ? `domain=${domain};` : "";
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};${domainPart}max-age=0;samesite=lax`;
      };
      deleteCookie(cookieName);
      deleteCookie(cookieName, "/", window.location.hostname);
    });
    try {
      sessionStorage.clear();
    } catch (e) {
      logger.warn("Failed to clear sessionStorage:", e);
    }
  }

  /**
   * Проверяет статус аутентификации через /api/v1/auth/status/.
   * Читает cookie-сессию eyehub_session, возвращает user + authenticated.
   */
  async checkAuthStatus() {
    try {
      const response = await fetch(`${API_BASE_PATH}${AUTH_ENDPOINTS.STATUS}`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        logger.warn("[AUTH] status check failed:", response.status);
        return { authenticated: false };
      }

      const data = await response.json();

      if (data.data?.authenticated) {
        return {
          authenticated: true,
          user: data.data.user,
          method: data.data.auth_method || "keycloak",
          keycloak_info: data.data.keycloak_info,
        };
      }
      return { authenticated: false };
    } catch (error) {
      // TypeError "Failed to fetch" — обычно browser aborts in-flight запрос при
      // навигации. warn, чтобы не алертить observability на recoverable случай.
      logger.warn("[AUTH] status check failed:", error);
      return { authenticated: false };
    }
  }

  /**
   * Возвращает текущего пользователя (или null).
   */
  async getCurrentUser() {
    try {
      const status = await this.checkAuthStatus();
      return status.authenticated && status.user ? status.user : null;
    } catch (error) {
      logger.error("[AUTH] getCurrentUser error:", error);
      return null;
    }
  }

  /**
   * Инициализация сервиса — единственный fetch к /auth/status/ на startup.
   */
  async initialize() {
    return this.checkAuthStatus();
  }

  /**
   * Обрабатывает callback аутентификации (возврат из Keycloak).
   */
  async handleCallback() {
    return bffAuthService.handleCallback();
  }

  /**
   * Проверяет, является ли текущий URL callback'ом Keycloak.
   */
  isCallbackUrl() {
    return bffAuthService.isCallbackUrl();
  }
}

export const authService = new AuthService();
export default authService;
